import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import { PropertyOverlay } from '../components/PropertyOverlay';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };
const DEFAULT_ZOOM = 15;

const PIN_COLORS = [
  { id: '#1B3B1B', label: 'Forest Green' },
  { id: '#B8975E', label: 'Brass' },
  { id: '#DC3545', label: 'Red' },
  { id: '#007BFF', label: 'Blue' },
  { id: '#FFC107', label: 'Gold' },
  { id: '#28A745', label: 'Green' },
  { id: '#6F42C1', label: 'Purple' },
  { id: '#FF6B35', label: 'Orange' },
  { id: '#20C997', label: 'Teal' },
  { id: '#E83E8C', label: 'Pink' },
];

const CAMP_FEATURES = [
  { id: 'fire_pit', label: 'Fire Pit' },
  { id: 'picnic_table', label: 'Picnic Table' },
  { id: 'outdoor_kitchen', label: 'Outdoor Kitchen' },
  { id: 'dock', label: 'Dock' },
  { id: 'boat_launch', label: 'Boat Launch' },
  { id: 'water_tank', label: 'Water Tank' },
  { id: 'solar', label: 'Solar' },
  { id: 'generator', label: 'Generator' },
  { id: 'wifi', label: 'WiFi' },
  { id: 'security', label: 'Security' },
  { id: 'hammock_spots', label: 'Hammock Spots' },
  { id: 'tent_spots', label: 'Tent Spots' },
  { id: 'atv_parking', label: 'ATV Parking' },
  { id: 'car_parking', label: 'Car Parking' },
  { id: 'wind_exposed', label: 'Wind Exposed' },
  { id: 'wind_sheltered', label: 'Wind Sheltered' },
  { id: 'trash_bins', label: 'Trash Bins' },
  { id: 'firewood_storage', label: 'Firewood Storage' },
  { id: 'propane', label: 'Propane' },
  { id: 'cell_signal', label: 'Cell Signal' },
];

const CABIN_FEATURES = [
  { id: 'wet_kitchen', label: 'Wet Kitchen' },
  { id: 'dry_kitchen', label: 'Dry Kitchen' },
  { id: 'bathroom', label: 'Bathroom' },
  { id: 'wood_stove', label: 'Wood Stove' },
  { id: 'propane_heater', label: 'Propane Heater' },
  { id: 'tv', label: 'TV' },
  { id: 'dining_table', label: 'Dining Table' },
  { id: 'refrigerator', label: 'Refrigerator' },
  { id: 'microwave', label: 'Microwave' },
  { id: 'couch', label: 'Couch' },
  { id: 'chairs', label: 'Chairs' },
  { id: 'bunk_beds', label: 'Bunk Beds' },
  { id: 'single_beds', label: 'Single Beds' },
  { id: 'electricity', label: 'Electricity' },
  { id: 'lighting', label: 'Lighting' },
  { id: 'shelving', label: 'Shelving' },
  { id: 'workbench', label: 'Workbench' },
  { id: 'loft', label: 'Loft' },
  { id: 'porch', label: 'Porch' },
  { id: 'lockable', label: 'Lockable' },
];

const CABIN_TYPES = [
  { id: 'bunk', label: 'Bunk' },
  { id: 'common', label: 'Common' },
  { id: 'close_storage', label: 'Close Storage' },
  { id: 'lodge', label: 'Lodge' },
  { id: 'outhouse', label: 'Outhouse' },
  { id: 'open_storage', label: 'Open Storage' },
];

const CONDITIONS = [
  { id: 'excellent', label: 'Excellent' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' },
  { id: 'poor', label: 'Poor' },
  { id: 'needs_repair', label: 'Needs Repair' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Camp {
  id: string;
  name: string;
  description: string | null;
  locationLat: string | null;
  locationLon: string | null;
  pinColor: string | null;
  features: string[];
  condition: string;
  cabinCount: number;
  cabins: Cabin[];
}

interface Cabin {
  id: string;
  name: string;
  campId: string;
  capacity: number;
  rooms: number | null;
  type: string;
  features: string[];
  condition: string;
}

type PanelMode =
  | { type: 'none' }
  | { type: 'confirm-pin'; lat: number; lng: number }
  | { type: 'create-camp'; lat: number; lng: number }
  | { type: 'edit-camp'; camp: Camp }
  | { type: 'view-camp'; camp: Camp }
  | { type: 'create-cabin'; campId: string }
  | { type: 'edit-cabin'; cabin: Cabin; campId: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ConditionBadge({ condition }: { condition: string }) {
  const colorMap: Record<string, string> = {
    excellent: '#28A745', good: '#28A745', fair: '#FFC107',
    poor: '#DC3545', needs_repair: '#DC3545',
  };
  return (
    <span style={{ ...s.badge, backgroundColor: `${colorMap[condition] || '#888'}22`, color: colorMap[condition] || '#888' }}>
      {condition.replace('_', ' ')}
    </span>
  );
}

function FeatureChecklist({ features, definitions, onChange }: {
  features: string[];
  definitions: { id: string; label: string }[];
  onChange: (f: string[]) => void;
}) {
  const toggle = (id: string) => onChange(features.includes(id) ? features.filter((f) => f !== id) : [...features, id]);
  return (
    <div style={s.checkGrid}>
      {definitions.map((d) => (
        <label key={d.id} style={s.checkItem}>
          <input type="checkbox" checked={features.includes(d.id)} onChange={() => toggle(d.id)} style={{ accentColor: COLORS.primaryGreen }} />
          <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>{d.label}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CampsPage() {
  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'none' });
  const [infoWindowId, setInfoWindowId] = useState<string | null>(null);

  // Camp form
  const [campName, setCampName] = useState('');
  const [campDesc, setCampDesc] = useState('');
  const [campPinColor, setCampPinColor] = useState('#1B3B1B');
  const [editLat, setEditLat] = useState<number | null>(null);
  const [editLng, setEditLng] = useState<number | null>(null);
  const [campFeatures, setCampFeatures] = useState<string[]>([]);
  const [campCondition, setCampCondition] = useState('good');

  // Cabin form
  const [cabinName, setCabinName] = useState('');
  const [cabinType, setCabinType] = useState('bunk');
  const [cabinCapacity, setCabinCapacity] = useState(0);
  const [cabinRooms, setCabinRooms] = useState(0);
  const [cabinFeatures, setCabinFeatures] = useState<string[]>([]);
  const [cabinCondition, setCabinCondition] = useState('good');


  useEffect(() => { loadCamps(); }, []);

  async function loadCamps() {
    setLoading(true);
    const res = await apiFetch<Camp[]>('/api/admin/camps');
    if (res.success && res.data) setCamps(res.data);
    setLoading(false);
  }

  // --- Map click: place pin ---
  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (panel.type !== 'none' && panel.type !== 'view-camp') return;
    if (!e.detail.latLng) return;
    setInfoWindowId(null);
    setPanel({ type: 'confirm-pin', lat: e.detail.latLng.lat, lng: e.detail.latLng.lng });
  }, [panel.type]);

  // --- Confirm pin -> open create form ---
  function confirmPin() {
    if (panel.type !== 'confirm-pin') return;
    setCampName('');
    setCampDesc('');
    setCampPinColor('#1B3B1B');
    setCampFeatures([]);
    setCampCondition('good');
    setPanel({ type: 'create-camp', lat: panel.lat, lng: panel.lng });
  }

  // --- Open edit form ---
  function openEditCamp(camp: Camp) {
    setInfoWindowId(null);
    setCampName(camp.name);
    setCampDesc(camp.description || '');
    setCampPinColor(camp.pinColor || '#1B3B1B');
    setCampFeatures(camp.features || []);
    setCampCondition(camp.condition || 'good');
    setEditLat(camp.locationLat ? parseFloat(camp.locationLat) : null);
    setEditLng(camp.locationLon ? parseFloat(camp.locationLon) : null);
    setPanel({ type: 'edit-camp', camp });
  }

  // --- Save camp ---
  async function saveCamp() {
    if (panel.type === 'create-camp') {
      await apiFetch('/api/admin/camps', {
        method: 'POST',
        body: JSON.stringify({
          name: campName,
          description: campDesc || undefined,
          location_lat: panel.lat,
          location_lon: panel.lng,
          pin_color: campPinColor,
          features: campFeatures,
          condition: campCondition,
        }),
      });
    } else if (panel.type === 'edit-camp') {
      await apiFetch(`/api/admin/camps/${panel.camp.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: campName,
          description: campDesc || undefined,
          location_lat: editLat,
          location_lon: editLng,
          pin_color: campPinColor,
          features: campFeatures,
          condition: campCondition,
        }),
      });
    }
    setPanel({ type: 'none' });
    loadCamps();
  }

  // --- Delete camp ---
  async function deleteCamp(camp: Camp) {
    if (!confirm(`Delete base camp "${camp.name}"?`)) return;
    await apiFetch(`/api/admin/camps/${camp.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    loadCamps();
  }

  // --- Cabin CRUD ---
  function openCreateCabin(campId: string) {
    setCabinName('');
    setCabinType('bunk');
    setCabinCapacity(0);
    setCabinRooms(0);
    setCabinFeatures([]);
    setCabinCondition('good');
    setPanel({ type: 'create-cabin', campId });
  }

  function openEditCabin(cabin: Cabin) {
    setCabinName(cabin.name);
    setCabinType(cabin.type);
    setCabinCapacity(cabin.capacity);
    setCabinRooms(cabin.rooms || 0);
    setCabinFeatures(cabin.features || []);
    setCabinCondition(cabin.condition);
    setPanel({ type: 'edit-cabin', cabin, campId: cabin.campId });
  }

  async function saveCabin() {
    if (panel.type === 'create-cabin') {
      await apiFetch('/api/admin/cabins', {
        method: 'POST',
        body: JSON.stringify({ name: cabinName, camp_id: panel.campId, type: cabinType, capacity: cabinCapacity, rooms: cabinRooms, features: cabinFeatures, condition: cabinCondition }),
      });
    } else if (panel.type === 'edit-cabin') {
      await apiFetch(`/api/admin/cabins/${panel.cabin.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: cabinName, type: cabinType, capacity: cabinCapacity, rooms: cabinRooms, features: cabinFeatures, condition: cabinCondition }),
      });
    }
    setPanel({ type: 'none' });
    loadCamps();
  }

  async function deleteCabin(cabin: Cabin) {
    if (!confirm(`Delete "${cabin.name}"?`)) return;
    await apiFetch(`/api/admin/cabins/${cabin.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    loadCamps();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={s.page}>
      {/* LEFT: Map */}
      <div style={s.mapCol}>
        <div style={s.mapHeader}>
          <h1 style={s.pageTitle}>Base Camps</h1>
          {panel.type === 'none' && (
            <span style={s.mapHint}>Click the map to place a new base camp</span>
          )}
        </div>
        <div style={{ ...s.mapWrap, position: 'relative' }}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              defaultCenter={PROPERTY_CENTER}
              defaultZoom={DEFAULT_ZOOM}
              mapTypeId="satellite"
              gestureHandling="greedy"
              fullscreenControl={false}
              mapId="grimsthorpe-admin-camps"
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%' }}
            >
              <PropertyOverlay />
              {/* Existing camp markers */}
              {camps.filter((c) => c.locationLat && c.locationLon).map((camp) => {
                const isEditing = panel.type === 'edit-camp' && panel.camp.id === camp.id;
                const lat = isEditing && editLat != null ? editLat : parseFloat(camp.locationLat!);
                const lng = isEditing && editLng != null ? editLng : parseFloat(camp.locationLon!);
                return (
                  <AdvancedMarker
                    key={camp.id}
                    position={{ lat, lng }}
                    title={camp.name}
                    draggable={isEditing}
                    onClick={() => {
                      if (!isEditing) {
                        setInfoWindowId(camp.id === infoWindowId ? null : camp.id);
                        setPanel({ type: 'view-camp', camp });
                      }
                    }}
                    onDragEnd={(e) => {
                      if (isEditing && e.latLng) {
                        setEditLat(e.latLng.lat());
                        setEditLng(e.latLng.lng());
                      }
                    }}
                  >
                    <Pin
                      background={camp.pinColor || '#1B3B1B'}
                      borderColor={isEditing ? '#00BFFF' : '#000'}
                      glyphColor="#FFF"
                    />
                  </AdvancedMarker>
                );
              })}

              {/* Info window */}
              {infoWindowId && (() => {
                const camp = camps.find((c) => c.id === infoWindowId);
                if (!camp || !camp.locationLat || !camp.locationLon) return null;
                return (
                  <InfoWindow
                    position={{ lat: parseFloat(camp.locationLat), lng: parseFloat(camp.locationLon) }}
                    onCloseClick={() => setInfoWindowId(null)}
                    pixelOffset={[0, -40]}
                  >
                    <div style={{ color: '#000', minWidth: 180 }}>
                      <strong style={{ fontSize: 14 }}>{camp.name}</strong>
                      {camp.description && <p style={{ margin: '4px 0', fontSize: 12 }}>{camp.description}</p>}
                      <p style={{ margin: '4px 0', fontSize: 11 }}>
                        {camp.cabinCount} building{camp.cabinCount !== 1 ? 's' : ''} &middot; {camp.condition?.replace('_', ' ')}
                      </p>
                      <button
                        onClick={() => { setInfoWindowId(null); openEditCamp(camp); }}
                        style={{ marginTop: 4, padding: '4px 12px', fontSize: 12, backgroundColor: '#1B3B1B', color: '#FFD700', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                      >
                        Edit Base Camp
                      </button>
                    </div>
                  </InfoWindow>
                );
              })()}

              {/* Pending pin (confirm placement) */}
              {panel.type === 'confirm-pin' && (
                <AdvancedMarker
                  position={{ lat: panel.lat, lng: panel.lng }}
                  draggable
                  onDragEnd={(e) => {
                    if (e.latLng) setPanel({ type: 'confirm-pin', lat: e.latLng.lat(), lng: e.latLng.lng() });
                  }}
                >
                  <Pin background="#FFC107" borderColor="#FF8F00" glyphColor="#000" />
                </AdvancedMarker>
              )}
            </GoogleMap>
          </APIProvider>
        </div>
      </div>

      {/* RIGHT: Panel */}
      <div style={s.panelCol}>
        {panel.type === 'none' && !loading && (
          <div style={s.panelEmpty}>
            <p style={{ color: COLORS.textMuted, fontSize: 13 }}>
              {camps.length === 0
                ? 'No base camps yet. Click the map to place your first one.'
                : `${camps.length} base camp${camps.length !== 1 ? 's' : ''} on the map. Click a pin to view or click the map to add a new one.`}
            </p>
            {/* Camp list */}
            {camps.map((camp) => (
              <div key={camp.id} style={s.campListItem} onClick={() => openEditCamp(camp)}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: camp.pinColor || '#1B3B1B', flexShrink: 0 }} />
                <div>
                  <div style={{ color: COLORS.textPrimary, fontWeight: 500, fontSize: 13 }}>{camp.name}</div>
                  <div style={{ color: COLORS.textMuted, fontSize: 11 }}>{camp.cabinCount} building{camp.cabinCount !== 1 ? 's' : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {panel.type === 'confirm-pin' && (
          <div style={s.panelContent}>
            <h3 style={s.panelTitle}>Confirm Location</h3>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>
              Drag the yellow pin to adjust, then confirm to create a base camp here.
            </p>
            <p style={{ color: COLORS.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>
              {panel.lat.toFixed(6)}, {panel.lng.toFixed(6)}
            </p>
            <div style={s.panelActions}>
              <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
              <button style={s.btnPrimary} onClick={confirmPin}>Confirm & Create</button>
            </div>
          </div>
        )}

        {(panel.type === 'create-camp' || panel.type === 'edit-camp') && (
          <div style={s.panelContent}>
            <h3 style={s.panelTitle}>{panel.type === 'edit-camp' ? 'Edit Base Camp' : 'New Base Camp'}</h3>
            <div style={s.field}>
              <label style={s.label}>Name</label>
              <input value={campName} onChange={(e) => setCampName(e.target.value)} style={s.input} autoFocus />
            </div>
            <div style={s.field}>
              <label style={s.label}>Description</label>
              <textarea value={campDesc} onChange={(e) => setCampDesc(e.target.value)} rows={2} style={{ ...s.input, resize: 'vertical' }} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Pin Color</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {PIN_COLORS.map((c) => (
                  <button
                    key={c.id}
                    title={c.label}
                    onClick={() => setCampPinColor(c.id)}
                    style={{
                      width: 28, height: 28, borderRadius: '50%', backgroundColor: c.id, border: campPinColor === c.id ? '3px solid #FFF' : '2px solid #333',
                      cursor: 'pointer', outline: campPinColor === c.id ? `2px solid ${c.id}` : 'none',
                    }}
                  />
                ))}
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Condition</label>
              <select value={campCondition} onChange={(e) => setCampCondition(e.target.value)} style={s.input}>
                {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Features</label>
              <FeatureChecklist features={campFeatures} definitions={CAMP_FEATURES} onChange={setCampFeatures} />
            </div>
            <div style={s.panelActions}>
              {panel.type === 'edit-camp' && (
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteCamp(panel.camp)}>Delete</button>
              )}
              <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
              <button style={s.btnPrimary} onClick={saveCamp} disabled={!campName.trim()}>
                {panel.type === 'edit-camp' ? 'Update' : 'Create'}
              </button>
            </div>

            {/* Cabins section (edit mode only) */}
            {panel.type === 'edit-camp' && (
              <div style={{ marginTop: 24, borderTop: `1px solid ${COLORS.borderDefault}`, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={s.label}>Buildings</label>
                  <button style={s.btnSmall} onClick={() => openCreateCabin(panel.camp.id)}>+ Add</button>
                </div>
                {panel.camp.cabins.length === 0 ? (
                  <p style={{ color: COLORS.textMuted, fontSize: 12, fontStyle: 'italic' }}>No buildings yet</p>
                ) : (
                  panel.camp.cabins.map((cabin) => (
                    <div key={cabin.id} style={s.cabinRow} onClick={() => openEditCabin(cabin)}>
                      <div>
                        <span style={{ color: COLORS.textPrimary, fontSize: 13, fontWeight: 500 }}>{cabin.name}</span>
                        <span style={{ color: COLORS.textMuted, fontSize: 11, marginLeft: 8 }}>
                          {CABIN_TYPES.find((t) => t.id === cabin.type)?.label}
                          {cabin.capacity > 0 ? ` · ${cabin.capacity} cap` : ''}
                        </span>
                      </div>
                      <ConditionBadge condition={cabin.condition} />
                    </div>
                  ))
                )}
              </div>
            )}

          </div>
        )}

        {panel.type === 'view-camp' && (
          <div style={s.panelContent}>
            <h3 style={s.panelTitle}>{panel.camp.name}</h3>
            {panel.camp.description && <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>{panel.camp.description}</p>}
            <ConditionBadge condition={panel.camp.condition || 'good'} />
            {panel.camp.features && panel.camp.features.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
                {panel.camp.features.map((f) => (
                  <span key={f} style={s.featureBadge}>{CAMP_FEATURES.find((d) => d.id === f)?.label || f}</span>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <label style={s.label}>Buildings ({panel.camp.cabins.length})</label>
              {panel.camp.cabins.map((cabin) => (
                <div key={cabin.id} style={s.cabinRow} onClick={() => openEditCabin(cabin)}>
                  <span style={{ color: COLORS.textPrimary, fontSize: 13 }}>{cabin.name}</span>
                  <ConditionBadge condition={cabin.condition} />
                </div>
              ))}
            </div>
            <div style={s.panelActions}>
              <button style={s.btnSmall} onClick={() => openCreateCabin(panel.camp.id)}>+ Add Building</button>
              <button style={s.btnPrimary} onClick={() => openEditCamp(panel.camp)}>Edit Base Camp</button>
            </div>
          </div>
        )}

        {(panel.type === 'create-cabin' || panel.type === 'edit-cabin') && (
          <div style={s.panelContent}>
            <h3 style={s.panelTitle}>{panel.type === 'edit-cabin' ? 'Edit Building' : 'New Building'}</h3>
            <div style={s.field}>
              <label style={s.label}>Name</label>
              <input value={cabinName} onChange={(e) => setCabinName(e.target.value)} style={s.input} autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...s.field, flex: 2 }}>
                <label style={s.label}>Type</label>
                <select value={cabinType} onChange={(e) => setCabinType(e.target.value)} style={s.input}>
                  {CABIN_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ ...s.field, flex: 1 }}>
                <label style={s.label}>Capacity</label>
                <input type="number" min="0" value={cabinCapacity} onChange={(e) => setCabinCapacity(parseInt(e.target.value, 10) || 0)} style={s.input} />
              </div>
              <div style={{ ...s.field, flex: 1 }}>
                <label style={s.label}>Rooms</label>
                <input type="number" min="0" value={cabinRooms} onChange={(e) => setCabinRooms(parseInt(e.target.value, 10) || 0)} style={s.input} />
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Condition</label>
              <select value={cabinCondition} onChange={(e) => setCabinCondition(e.target.value)} style={s.input}>
                {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Features</label>
              <FeatureChecklist features={cabinFeatures} definitions={CABIN_FEATURES} onChange={setCabinFeatures} />
            </div>
            <div style={s.panelActions}>
              {panel.type === 'edit-cabin' && (
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteCabin(panel.cabin)}>Delete</button>
              )}
              <button style={s.btnSecondary} onClick={() => {
                const campId = panel.type === 'edit-cabin' ? panel.campId : panel.campId;
                const camp = camps.find((c) => c.id === campId);
                if (camp) setPanel({ type: 'edit-camp', camp }); else setPanel({ type: 'none' });
              }}>Back</button>
              <button style={s.btnPrimary} onClick={saveCabin} disabled={!cabinName.trim()}>
                {panel.type === 'edit-cabin' ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        )}

        {loading && <div style={{ color: COLORS.textMuted, padding: 24 }}>Loading...</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  mapHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', flexShrink: 0 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  mapHint: { fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic' },
  mapWrap: { flex: 1, borderRadius: 8, overflow: 'hidden', margin: '0 0 0 16px' },
  panelCol: { width: 380, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelEmpty: { padding: 20 },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  checkGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  featureBadge: { display: 'inline-block', padding: '2px 8px', backgroundColor: 'rgba(184,151,94,0.15)', color: COLORS.textGold, borderRadius: 4, fontSize: 11 },
  campListItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginTop: 6, borderRadius: 8, border: `1px solid ${COLORS.borderDefault}`, cursor: 'pointer' },
  cabinRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', marginTop: 4, borderRadius: 6, border: `1px solid ${COLORS.borderDefault}`, cursor: 'pointer' },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSmall: { padding: '4px 10px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 500, cursor: 'pointer' },
};
